import React, { useState } from 'react';
import { useSummarizer } from '@core/store/useSummarizer';
import { SOURCE_TYPES } from '../../../../constants/summarizer';
import { Link2, Sparkles } from 'lucide-react';
import { SummaryOptionsBar, ProcessingButton } from '@components/ui';

interface URLConfig {
  enabled: boolean;
  placeholder?: string;
  validation?: RegExp;
  examples?: string[];
  helpText?: string;
  showExamples?: boolean;
  showHelpText?: boolean;
}

interface URLInputProps {
  config: URLConfig;
  disabled?: boolean;
  className?: string;
  primaryColor?: string;
  submitButtonText?: string;
  showSummaryOptions?: boolean;
  showSummaryType?: boolean;
  showTone?: boolean;
}

const URLInput: React.FC<URLInputProps> = ({
  config,
  disabled = false,
  className = '',
  primaryColor = '#3b82f6',
  submitButtonText = 'Generate Summary',
  showSummaryOptions = true,
  showSummaryType = true,
  showTone = true,
}) => {
  const {
    urlInput,
    urlError,
    summaryType,
    tone,
    setUrlInput,
    validateUrl,
    clearError,
    setSummaryType,
    setTone,
    summarize,
    isLoading: storeLoading,
    setUrlError,
  } = useSummarizer();

  const handleSubmit = async () => {
    if (!urlInput.trim()) return;

    // Clear any existing errors first
    clearError();
    setUrlError(null);

    // Validate URL
    const isValid = validateUrl(urlInput, config.validation);
    if (!isValid) {
      let errorMessage = 'Please enter a valid URL';

      if (config.validation) {
        errorMessage = `Please enter a valid URL that matches the expected format (e.g., ${config.examples?.[0] || 'https://example.com'})`;
      }

      setUrlError(errorMessage);
      return;
    }

    try {
      await summarize({
        link_or_id: urlInput.trim(),
        source: SOURCE_TYPES.URL,
        summaryType,
        tone,
      });
    } catch (error) {
      // Error is handled by the store
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !storeLoading && !disabled && urlInput.trim()) {
      handleSubmit();
    }
  };

  const handleExampleClick = (example: string) => {
    setUrlInput(example);
    clearError();
  };

  const canSubmit = urlInput.trim() && !urlError && !storeLoading && !disabled;
  const loading = storeLoading;

  const [showExamples, setShowExamples] = useState(false);

  return (
    <div className={`url-input space-y-3 ${className}`}>
      {/* URL Input Field with Icon */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
          <Link2 className="w-5 h-5" />
        </div>
        <input
          type="url"
          value={urlInput}
          onChange={(e) => {
            if (urlError) {
              clearError(); // Clear error when user starts typing
              setUrlError(null);
            }
            setUrlInput(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          placeholder={config.placeholder || 'Enter URL...'}
          disabled={loading || disabled}
          className={`w-full !h-16 outline-none  !pl-10 !pr-12 py-3 !border !rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
            urlError
              ? 'border-red-400 focus:ring-red-500 bg-red-50'
              : `border-gray-300 focus:ring-[${primaryColor}] focus:!bg-white focus:!text-black`
          } ${loading || disabled ? 'bg-gray-50 cursor-not-allowed' : urlError ? 'bg-red-50' : 'bg-white'}`}
        />
      </div>

      {/* Error Display - Positioned immediately after input field */}
      {urlError && (
        <div className="p-4 bg-red-50 border-l-4 border-red-400 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">URL validation failed</p>
              <div className="mt-1 text-sm text-red-700">{urlError}</div>
            </div>
          </div>
        </div>
      )}

      {config.helpText && config.showHelpText && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">{config.helpText}</p>
        </div>
      )}

      {/* Example URLs */}
      {config.examples && config.examples.length > 0 && config.showExamples && (
        <div>
          <button
            onClick={() => setShowExamples(!showExamples)}
            className="text-sm border-none focus:!outline-none bg-transparent text-gray-500 hover:!bg-transparent active:!border-none active:!ring-0 hover:text-gray-700 flex items-center !gap-2 focus:!text-gray-500 focus:!bg-transparent focus:!ring-0"
          >
            <Sparkles className="w-3 h-3" />
            {showExamples ? 'Hide' : 'Show'} examples
          </button>
          {showExamples && (
            <div className="mt-2 space-y-1">
              {config.examples.map((example, index) => (
                <div key={index}>
                  <p
                    onClick={() => handleExampleClick(example)}
                    className="w-full text-left text-xs p-2 hover:bg-gray-100 rounded transition-colors truncate cursor-pointer inline"
                    title={example}
                  >
                    <span className="text-gray-400 mr-1">→</span>
                    {example}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2 sm:!flex-row items-center justify-between">
        {/* Summary Options */}
        {showSummaryOptions && (
          <SummaryOptionsBar
            summaryType={summaryType}
            tone={tone}
            onSummaryTypeChange={setSummaryType}
            onToneChange={setTone}
            disabled={loading || disabled}
            containerClassName="justify-between sm:justify-normal w-full"
            className="flex !flex-row gap-3"
            summaryTypeProps={{ triggerClassName: 'w-full max-w-[160px] bg-white' }}
            toneProps={{ triggerClassName: 'w-full max-w-[160px] bg-white' }}
            showSummaryType={showSummaryType}
            showTone={showTone}
          />
        )}

        {/* Enhanced Submit Button */}
        <ProcessingButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          isLoading={loading}
          submitButtonText={submitButtonText}
          className="sm:ml-auto w-full sm:w-auto sm:min-w-[180px]"
        />
      </div>
    </div>
  );
};

export default URLInput;
